import { CustomModal } from "@/components/Modal";
import { PageTitle } from "@/components/Title";
import Stack from "@mui/material/Stack";
import { Box, Button as MuiButton, Link, Typography } from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useEffect, useState } from "react";

interface IProps {
  open: boolean;
  handleClose: () => void;
  handleSubmit: (cancelLoading: () => void) => void;
  isReadOnly: boolean;
  timeout?: number;
}

/**
 * 取得済み商業登記からの後追い解析の確認ダイアログ。
 * 不動産 acquireBookStatus/components/ConfirmAnalyzeDialog.tsx の見た目を踏襲する
 * （商業登記には全部事項のような解析オプションの内訳が無いため、オプション選択UIは持たない）。
 */
export const ConfirmAnalyzeDialog: React.FC<IProps> = (props: IProps) => {
  const { open, handleClose, handleSubmit, isReadOnly } = props;

  // 解析リクエストの二重送信を防ぐ。送信後は結果一覧へ遷移するが、遷移前の連打対策として無効化する。
  // handleSubmit に渡す cancelLoading が呼ばれた場合（失敗等で画面が残る場合）は再度押せるよう戻す
  const [isSubmitting, setIsSubmitting] = useState(false);

  // モーダルを開き直したときは送信状態をリセットする
  useEffect(() => {
    if (open) setIsSubmitting(false);
  }, [open]);

  return (
    <CustomModal
      isOpen={open}
      handleClose={(_event, reason) => {
        // モーダル外クリック、またはエスケープキー押下では閉じない
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      ariaLabelledby="confirm-analyze-dialog"
      ariaDescribedby="confirm-analyze-dialog-description"
      width={{ xs: "90vw", sm: 460 }}
      maxHeight="90vh"
      overflow="auto"
    >
      <Stack direction={"column"} spacing={3}>
        {/* タイトル */}
        <Box id="confirm-analyze-dialog">
          <PageTitle>確認</PageTitle>
        </Box>
        {/* テキスト */}
        <Box>
          <Typography id="confirm-analyze-dialog-description" variant="body1">
            選択した商業登記の解析を行います。
          </Typography>
        </Box>
        {/* 注意書き */}
        <Typography variant="body1">
          「解析する」ボタンを押すと、解析を実行し、結果一覧画面へ移動します。
          <br />
          ※解析が完了するまでお時間がかかる場合がございます。「更新」ボタンを押して最新の結果をご確認ください。
        </Typography>
        {/* フッター（閉じる=テキストリンク / 解析する=primary outline ボタン） */}
        <Stack
          direction={"row"}
          spacing={3}
          marginTop={2}
          alignItems={"center"}
          justifyContent={"flex-end"}
        >
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={handleClose}
            sx={{
              color: "gray.main",
              fontSize: "0.875rem",
            }}
          >
            閉じる
          </Link>
          <ReadOnlyTooltip isReadOnly={isReadOnly}>
            <MuiButton
              variant="outlined"
              color="primary"
              disabled={isReadOnly || isSubmitting}
              onClick={() => {
                setIsSubmitting(true);
                // 失敗等で画面が残る場合は再度押せるよう、cancelLoading で送信状態を戻す
                handleSubmit(() => {
                  setIsSubmitting(false);
                });
              }}
            >
              解析する
            </MuiButton>
          </ReadOnlyTooltip>
        </Stack>
      </Stack>
    </CustomModal>
  );
};
